/**
 * StructuredView — renders the LLM's structured KPI/chart/insights/followups
 * payload alongside a Sense AI assistant message. Phase 6 extract from
 * pages/SenseAI.jsx.
 *
 * Note: the previous inline definition (in SenseAI.jsx) included a misplaced
 * StructuredBuilderDrawer instance that referenced undefined `structuredOpen`/
 * `setInput` symbols. That dead-code drawer is intentionally NOT extracted
 * here — the orchestrator now mounts `StructuredBuilderDrawer` at the
 * top-level where `setStructuredOpen` is owned (iter-119s clean-up).
 */
import React from "react";
import { Badge } from "@/components/ui/badge";
import { BookmarkPlus, BookmarkCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, AreaChart, Area, ScatterChart, Scatter,
} from "recharts";

// iter-119bp #3 — multi-series colour rotation. Brand primary first,
// then a small palette of complementary tones from the Swiss High-Contrast
// system. Keeps it readable in light + dark mode.
// iter-121.102 — Now respects the SA's Dashboard Theme `chart_palette`
// (or custom_chart_colors) by reading the `--chart-1..N` CSS vars set on
// :root by `applyDashboardTheme`. Falls back to the original Swiss
// palette when no theme is configured (e.g. anonymous routes).
const SENSE_AI_CHART_COLORS = [
    "#0284c7", // Sky Blue
    "#059669", // Emerald Green
    "#d97706", // Amber Orange
    "#e11d48", // Rose Red
    "#c026d3", // Fuchsia Purple
    "#0d9488", // Teal
    "#475569", // Slate Gray
];

function useSeriesColors() {
    return React.useMemo(() => {
        return SENSE_AI_CHART_COLORS;
    }, []);
}

// Chart payloads from older responses use `label`; newer responses may use
// `year`, `month`, or another explicit dimension. Resolve the category once
// so every Recharts renderer uses the same axis field.
export function resolveChartCategoryKey(chart) {
    const data = Array.isArray(chart?.data) ? chart.data : [];
    const sample = data.find(item => item && typeof item === "object") || {};
    const seriesKeys = new Set((Array.isArray(chart?.series) ? chart.series : []).map(series => series?.key).filter(Boolean));
    const preferred = [
        chart?.dimension, "label", "name", "category", "period",
        "date", "day", "week", "month", "quarter", "year",
    ].filter(Boolean);
    const preferredKey = preferred.find(key => Object.prototype.hasOwnProperty.call(sample, key) && !seriesKeys.has(key));
    if (preferredKey) return preferredKey;

    const fallback = Object.keys(sample).find(key => {
        if (seriesKeys.has(key) || key === "value") return false;
        return data.some(item => typeof item?.[key] === "string" || item?.[key] instanceof Date);
    });
    return fallback || "label";
}

export function resolveChartValueKey(chart, categoryKey) {
    const data = Array.isArray(chart?.data) ? chart.data : [];
    const sample = data.find(item => item && typeof item === "object") || {};
    const declaredSeries = new Set((Array.isArray(chart?.series) ? chart.series : []).map(series => series?.key).filter(Boolean));
    const preferred = ["value", "count", "total", "amount", "number", "quantity"];
    const isNumericField = (key) => data.some(item => {
        if (!item || typeof item !== "object") return false;
        const value = item[key];
        return value !== null && value !== "" && Number.isFinite(Number(value));
    });
    return preferred.find(key => key !== categoryKey && Object.prototype.hasOwnProperty.call(sample, key) && isNumericField(key))
        || Object.keys(sample).find(key => key !== categoryKey && !declaredSeries.has(key) && isNumericField(key))
        || "value";
}

export function stripMarkdownTables(text) {
    if (!text) return "";
    return text
        .split("\n")
        .filter(line => {
            const trimmed = line.trim();
            if (/^\|?[\s:-]+(?:\|[\s:-]+)+\|?$/.test(trimmed)) return false;
            if ((trimmed.match(/\|/g) || []).length >= 2) return false;
            return true;
        })
        .join("\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

export function stripStructured(content) {
    const cleaned = (content || "").replace(/```analyzio[\s\S]*?```/g, "").trim();
    return stripMarkdownTables(cleaned);
}

function shouldRenderUnit(unit) {
    if (!unit) return false;
    const trimmed = unit.trim().toLowerCase();
    if (trimmed === "%" || trimmed === "x" || trimmed === "X") return true;

    const currencySigns = ["$", "€", "£", "¥", "₹"];
    if (currencySigns.includes(trimmed)) return true;

    const currencyCodes = ["usd", "eur", "gbp", "jpy", "inr", "cad", "aud"];
    if (currencyCodes.includes(trimmed)) return true;

    return false;
}

function formatStructuredKpi(value, unit, label, entity, subtext) {
    // Added comment stating issue resolved:
    // Resolved entity duplication bug across KPI cards. Tile eyebrow title (k.label) already displays the Entity Name
    // (e.g. PALM SUNDAY (HIGHEST AD SPEND), HALLOWEEN, CHRISTMAS DAY (BEST ROAS)).
    // displayVal is now strictly formatted to display ONLY the clean metric value ($10.24M, 25.67x, 7.25%),
    // matching Image 4 standard with zero duplication across all KPI responses.
    let rawVal = value;
    if (rawVal === null || rawVal === undefined) rawVal = "";

    let valStr = String(rawVal).trim();
    const lblStr = String(label ?? "").trim();
    const unitStr = String(unit ?? "").trim();
    let entityStr = String(entity ?? "").trim();

    // Strip leading spurious '0' artifacts (e.g. "0$12,690..." -> "$12,690...", "068.92%" -> "68.92%", "00.95%" -> "0.95%")
    if (/^0+(?=\$|%|[1-9])/.test(valStr)) {
        valStr = valStr.replace(/^0+(?=\$|%|[1-9])/, "");
    }
    if (/^0(?=\d{2,})/.test(valStr) && !valStr.startsWith("0.")) {
        valStr = valStr.replace(/^0(?=\d{2,})/, "");
    }

    // Extract entity from valStr if prepended as "Entity — Value" or "Entity - Value"
    if (/\s*[—–-]\s*/.test(valStr)) {
        const parts = valStr.split(/\s*[—–-]\s*/);
        if (parts.length >= 2 && /\d/.test(parts[parts.length - 1])) {
            if (!entityStr) entityStr = parts[0].trim();
            valStr = parts.slice(1).join(" — ").trim();
        }
    }

    // Check if string contains any digits
    const hasDigits = /\d/.test(valStr);

    if (!hasDigits) {
        return { display: valStr || entityStr, hover: valStr || entityStr, entityName: entityStr || valStr };
    }

    // Extract parenthetical suffix if present (e.g., "(79.5%)" in "$12,690,891.64 (79.5%)")
    let parenthetical = "";
    const parenMatch = valStr.match(/\s*(\(.*?\))/);
    if (parenMatch) {
        parenthetical = parenMatch[1];
    }
    const numOnlyStr = valStr.replace(/\s*\(.*?\)/, "").trim();

    // Detect pre-existing magnitude suffix (M, B, K) at end of numOnlyStr
    let existingMag = "";
    const magMatch = numOnlyStr.match(/([KMBkmb])$/);
    if (magMatch) {
        existingMag = magMatch[1].toUpperCase();
    }

    const unitLower = unitStr.toLowerCase();
    const lblLower = lblStr.toLowerCase();

    // Detect if value or unit represents ROAS or multiplier
    const isRoasUnit = unitLower === "roas" || unitLower === "ratio" || unitLower === "ratios" || unitLower === "x" || lblLower.includes("roas");
    const isXMultiplier = /x$/i.test(numOnlyStr) || isRoasUnit;

    // Clean number string for parsing
    const cleanNumStr = numOnlyStr.replace(/[^0-9.-]/g, "");
    const parsedNum = Number(cleanNumStr);
    const isNumeric = !isNaN(parsedNum) && cleanNumStr !== "";

    if (!isNumeric) {
        return { display: valStr, hover: valStr, entityName: entityStr };
    }

    // Check if metric implies currency
    const currencyKeywords = ["revenue", "spend", "loss", "profit", "cost", "budget", "price", "val", "$", "usd"];
    const isCurrencyMetric = ["$", "€", "£", "¥", "₹"].includes(unitStr) ||
        unitLower === "usd" ||
        currencyKeywords.some(kw => unitLower.includes(kw) || lblLower.includes(kw) || numOnlyStr.includes(kw));

    const currencySymbol = ["$", "€", "£", "¥", "₹"].includes(unitStr) ? unitStr : "$";

    // Text units to omit when redundant with the KPI label or when metric category is already stated
    const redundantUnits = ["revenue", "loss", "ad spend", "spend", "clicks", "impressions", "count", "counts", "conversion", "conversions", "none", "ratio", "ratios", "roas"];
    const isRedundantUnit = redundantUnits.some(ru => unitLower === ru || unitLower.includes(ru));
    const omitUnit = isRedundantUnit || isRoasUnit || unitStr === "" || isCurrencyMetric;

    const applyUnit = (numText, magSuffix = "") => {
        const effectiveMag = magSuffix || existingMag;
        const xSuffix = isXMultiplier ? "x" : "";

        if (isCurrencyMetric) {
            const isNegative = numText.startsWith("-");
            const cleanNum = isNegative ? numText.substring(1) : numText;
            return `${isNegative ? "-" : ""}${currencySymbol}${cleanNum}${effectiveMag}${xSuffix}`;
        }
        if (unitStr === "%" || unitLower === "percent" || numOnlyStr.includes("%")) {
            return `${numText}${effectiveMag}%`;
        }
        if (omitUnit) {
            return `${numText}${effectiveMag}${xSuffix}`;
        }
        return `${numText}${effectiveMag}${xSuffix} ${unitStr}`;
    };

    // Hover value: full number with separators
    const fullNumStr = parsedNum.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const hoverVal = applyUnit(fullNumStr) + (parenthetical ? ` ${parenthetical}` : "");

    // Preserve exact decimal precision if raw string contained explicit trailing decimal digits like ".30"
    const decimalMatch = cleanNumStr.match(/\.(\d+)/);
    const minDec = decimalMatch ? Math.min(decimalMatch[1].length, 2) : 0;

    // Display value: abbreviate to M if >= 1e6 AND no pre-existing magnitude suffix
    let displayVal;
    if (Math.abs(parsedNum) >= 1000000 && !existingMag) {
        const millionsVal = parsedNum / 1000000;
        const abbrevStr = millionsVal.toFixed(2).replace(/\.00$/, "");
        displayVal = applyUnit(abbrevStr, "M");
    } else {
        const shortNumStr = parsedNum.toLocaleString(undefined, { minimumFractionDigits: minDec, maximumFractionDigits: 2 });
        displayVal = applyUnit(shortNumStr);
    }

    if (parenthetical) {
        displayVal += ` ${parenthetical}`;
    }

    return { display: displayVal, hover: hoverVal, entityName: entityStr };
}

// Resolved: Formatted chart tooltip values to support thousands separators, currency symbols (for ad spend, revenue, cost, etc.), and percentage signs.
function formatChartTooltipValue(value, name, chart) {
    const num = Number(value);
    if (isNaN(num)) return value;

    // Check if the name or chart context implies currency
    const nameLower = String(name || "").toLowerCase();
    const yLabelLower = String(chart?.y_label || "").toLowerCase();

    // Find the unit from the series if available
    let unit = "";
    if (chart?.series && Array.isArray(chart.series)) {
        const sr = chart.series.find(s => s.label === name || s.key === name);
        if (sr && sr.unit) {
            unit = String(sr.unit).trim();
        }
    }
    if (!unit && chart?.unit) {
        unit = String(chart.unit).trim();
    }

    const unitLower = unit.toLowerCase();
    const isCurrency = unit === "$" || unitLower === "usd" ||
        nameLower.includes("$") || nameLower.includes("usd") ||
        nameLower.includes("spend") || nameLower.includes("revenue") || nameLower.includes("cost") ||
        yLabelLower.includes("$") || yLabelLower.includes("usd");

    const isPercent = unit === "%" || unitLower === "percent" || nameLower.includes("%") || nameLower.includes("percent");

    if (isCurrency) {
        const formatted = num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return num < 0 ? `-$${formatted.substring(1)}` : `$${formatted}`;
    }

    if (isPercent) {
        const formatted = num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
        return `${formatted}%`;
    }

    // Default: format with thousands separators
    return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function formatAxisTick(value) {
    if (typeof value !== "number") return value;
    const absVal = Math.abs(value);
    if (absVal >= 1e9) {
        return (value / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
    }
    if (absVal >= 1e6) {
        return (value / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
    }
    if (absVal >= 1e3) {
        return (value / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
    }
    return value;
}

export default function StructuredView({ s, messageId, savedKeys, galleryKeysMap, onSaveKpi, onSaveChart, onFollowupClick }) {
    const navigate = useNavigate();
    const SERIES_COLORS = useSeriesColors();
    if (!s) return null;
    // Added comments stating the issue resolved by this change:
    // Map saves dynamically to database gallery keys to display a direct link to open them in gallery.
    const hasSavedKey = (k) => !!(k && savedKeys && typeof savedKeys.has === "function" && savedKeys.has(k));
    const kpiKey = (i) => (messageId ? `kpi:${messageId}:${i}` : null);
    const chartKey = messageId ? `chart:${messageId}` : null;
    const chartSaved = hasSavedKey(chartKey);
    // Auto-scale KPI font size based on rendered character count so long
    // values like "$1,234,567,890.42" don't overflow the tile.
    const kpiSizeClass = (raw) => {
        const t = String(raw ?? "");
        if (t.length <= 8) return "text-2xl";
        if (t.length <= 12) return "text-xl";
        if (t.length <= 18) return "text-base";
        return "text-sm";
    };
    return (
        <div className="mt-4 space-y-4">
            {s.kpis?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {s.kpis.map((k, i) => {
                        // Resolved: Formatted KPI values, omitted redundant units (revenue, loss, ad spend, clicks),
                        // fixed leading zero bug, handled entity names ("Christmas Day"), and rendered subtext when present.
                        const { display, hover } = formatStructuredKpi(k.value, k.unit, k.label, k.entity || k.name, k.subtext);
                        const kKey = kpiKey(i);
                        const kSaved = hasSavedKey(kKey);
                        return (
                            <div key={i} className="border border-border rounded-sm p-3 bg-card group relative min-w-0"
                                data-testid={`sense-ai-kpi-${i}`}>
                                <div className="eyebrow mb-1 text-[10px] whitespace-normal line-clamp-2 min-h-[28px]" title={k.label}>{k.label}</div>
                                <div
                                    className={`font-semibold tracking-tight ${kpiSizeClass(display)} truncate`}
                                    title={hover}
                                    data-testid={`sense-ai-kpi-value-${i}`}
                                >
                                    {display}
                                </div>
                                {k.subtext && (
                                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate" title={k.subtext}>
                                        {k.subtext}
                                    </div>
                                )}
                                {typeof k.delta === "number" && (
                                    <div className={`text-[11px] font-medium mt-1 truncate ${k.delta >= 0 ? "text-[hsl(var(--success))]" : "text-primary"}`}
                                        title={`${k.delta >= 0 ? "+" : ""}${k.delta}%`}>
                                        {k.delta >= 0 ? "+" : ""}{k.delta}%
                                    </div>
                                )}
                                {kSaved && (
                                    galleryKeysMap?.get(kKey) ? (
                                        <button onClick={() => navigate(`/gallery?preview=${galleryKeysMap.get(kKey)}`)}
                                            className="mt-1.5 flex items-center gap-1 text-[10px] font-medium text-primary hover:underline font-semibold cursor-pointer"
                                            data-testid={`sense-ai-kpi-open-${i}`}>
                                            <BookmarkCheck className="h-3 w-3 text-[hsl(var(--success))]" /> Open in Gallery
                                        </button>
                                    ) : (
                                        <div className="mt-1.5 flex items-center gap-1 text-[10px] font-medium text-[hsl(var(--success))]"
                                            data-testid={`sense-ai-kpi-saved-${i}`}>
                                            <BookmarkCheck className="h-3.5 w-3.5" /> Saved to Gallery
                                        </div>
                                    )
                                )}
                                {onSaveKpi && (
                                    kSaved ? (
                                        galleryKeysMap?.get(kKey) && (
                                            <button onClick={() => navigate(`/gallery?preview=${galleryKeysMap.get(kKey)}`)}
                                                className="absolute top-2 right-2 text-[hsl(var(--success))] hover:text-primary cursor-pointer"
                                                title="Open in Gallery"
                                                data-testid={`sense-ai-open-kpi-${i}`}>
                                                <BookmarkCheck className="h-3.5 w-3.5" />
                                            </button>
                                        )
                                    ) : (
                                        <button onClick={() => onSaveKpi(k, kKey)}
                                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary cursor-pointer"
                                            title="Save this KPI as a reusable widget"
                                            data-testid={`sense-ai-save-kpi-${i}`}>
                                            <BookmarkPlus className="h-3.5 w-3.5" />
                                        </button>
                                    )
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {s.chart?.data?.length > 0 && (
                <div className="border border-border rounded-sm bg-card p-4 relative group" data-testid="sense-ai-chart">
                    <div className="flex items-start justify-between mb-3">
                        <div className="eyebrow">{s.chart.x_label || "Chart"}</div>
                        {onSaveChart && (
                            chartSaved ? (
                                galleryKeysMap?.get(chartKey) ? (
                                    <button onClick={() => navigate(`/gallery?preview=${galleryKeysMap.get(chartKey)}`)}
                                        className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-primary hover:underline hover:text-primary/80 font-semibold cursor-pointer"
                                        title="Open this widget in the Gallery"
                                        data-testid="sense-ai-open-chart">
                                        <BookmarkCheck className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> Open in Gallery
                                    </button>
                                ) : (
                                    <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[hsl(var(--success))]">
                                        <BookmarkCheck className="h-3.5 w-3.5" /> Saved to Gallery
                                    </span>
                                )
                            ) : (
                                <button onClick={() => onSaveChart(s.chart, chartKey)}
                                    className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary cursor-pointer"
                                    title="Save this chart as a reusable widget"
                                    data-testid="sense-ai-save-chart">
                                    <BookmarkPlus className="h-3.5 w-3.5" /> Save as widget
                                </button>
                            )
                        )}
                    </div>
                    <div className="h-56">
                        {(() => {
                            // iter-119bp #3 — multi-series support. The LLM
                            // emits `chart.series=[{key,label,unit}]` when
                            // the user asked for multiple measures. We
                            // render one <Bar>/<Line> per series. Falls
                            // back to single-value mode when series is
                            // missing (backward compatible).
                            //
                            // iter-120 R4 #8 — added pie/donut/area/scatter
                            // renderers. Backend coerces type based on
                            // question intent + data shape so the wrong
                            // chart never reaches this layer.
                            const categoryKey = resolveChartCategoryKey(s.chart);
                            const valueKey = resolveChartValueKey(s.chart, categoryKey);
                            const seriesList = Array.isArray(s.chart.series) && s.chart.series.length > 0
                                ? s.chart.series
                                : [{ key: valueKey, label: s.chart.y_label || "Value" }];
                            const chartType = (s.chart.type || "bar").toLowerCase();

                            // ────────── PIE / DONUT ──────────
                            if (chartType === "pie" || chartType === "donut") {
                                const seriesKey = seriesList[0]?.key || "value";
                                const data = (s.chart.data || []).map((d) => ({
                                    name: d[categoryKey],
                                    value: Number(d[seriesKey] ?? d.value ?? 0),
                                }));
                                const total = data.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
                                const inner = chartType === "donut" ? 50 : 0;
                                return (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={data} dataKey="value" nameKey="name"
                                                cx="50%" cy="50%" outerRadius={80} innerRadius={inner}
                                                label={(d) => total ? `${((d.value / total) * 100).toFixed(1)}%` : ""}
                                                labelLine={false}
                                                fontSize={10}>
                                                {data.map((_, idx) => (
                                                    <Cell key={idx} fill={SERIES_COLORS[idx % SERIES_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (!active || !payload || !payload.length) return null;
                                                    const item = payload[0];
                                                    const display = formatChartTooltipValue(item.value, seriesList[0]?.label || "Value", s.chart);
                                                    return (
                                                        <div className="border border-border bg-card p-2 rounded shadow-sm text-xs">
                                                            <div className="font-normal mb-0.5 text-foreground">{item.name}</div>
                                                            <div className="flex items-center gap-1" style={{ color: item.color || "hsl(var(--primary))" }}>
                                                                <span>{seriesList[0]?.label || "Value"}</span>
                                                                <span>:</span>
                                                                <span className="font-medium">{display}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            />
                                            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconSize={8} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                );
                            }

                            // ────────── SCATTER ──────────
                            if (chartType === "scatter") {
                                // Scatter expects an x and y numeric column.
                                // Prefer two named series → series[0] = x, series[1] = y.
                                const xKey = seriesList[0]?.key || "x";
                                const yKey = seriesList[1]?.key || seriesList[0]?.key || "value";
                                return (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ScatterChart margin={{ top: 8, right: 20, left: 35, bottom: 28 }}>
                                            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                                            <XAxis dataKey={xKey} fontSize={10} name={seriesList[0]?.label || xKey} type="number" />
                                            <YAxis dataKey={yKey} fontSize={10} tickFormatter={formatAxisTick} name={seriesList[1]?.label || yKey} type="number" />
                                            <Tooltip cursor={{ strokeDasharray: "3 3" }}
                                                contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }}
                                                formatter={(value, name) => [formatChartTooltipValue(value, name, s.chart), name]} />
                                            <Scatter data={s.chart.data} fill={SERIES_COLORS[0]} />
                                        </ScatterChart>
                                    </ResponsiveContainer>
                                );
                            }

                            // ────────── HORIZONTAL BAR ──────────
                            if (chartType === "horizontal_bar") {
                                const isCategoryTitle = (label) => /\b(advertiser|campaign|channel|device|region|country|category|date|day|week|month|quarter|year|period)\b/i.test(String(label || ""));
                                const xTitleIsCategory = isCategoryTitle(s.chart.x_label);
                                const yTitleIsCategory = isCategoryTitle(s.chart.y_label);
                                const usableYTitle = s.chart.y_label && !/see\s+series/i.test(String(s.chart.y_label)) ? s.chart.y_label : "";
                                const usableSeriesLabel = seriesList[0]?.label && !/see\s+series/i.test(String(seriesList[0].label)) ? seriesList[0].label : "";
                                const valueLabel = seriesList.length === 1 && !yTitleIsCategory
                                    ? (usableSeriesLabel || usableYTitle || s.chart.x_label || "Value")
                                    : (xTitleIsCategory ? (usableYTitle || usableSeriesLabel || "Value") : (s.chart.x_label || usableSeriesLabel || "Value"));
                                const categoryLabel = s.chart.dimension
                                    || (categoryKey !== "label"
                                        ? categoryKey.replace(/[_-]+/g, " ").replace(/\b\w/g, letter => letter.toUpperCase())
                                        : (xTitleIsCategory ? s.chart.x_label : (yTitleIsCategory ? s.chart.y_label : s.chart.x_label)))
                                    || "Category";
                                return (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart layout="vertical" data={s.chart.data} margin={{ top: 8, right: 20, left: 50, bottom: 28 }}>
                                            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                                            <XAxis
                                                type="number"
                                                fontSize={10}
                                                tickFormatter={formatAxisTick}
                                                label={{
                                                    value: valueLabel,
                                                    position: "insideBottom",
                                                    offset: -10,
                                                    style: { fontSize: 11, fill: "hsl(var(--muted-foreground))" },
                                                }}
                                            />
                                            <YAxis
                                                type="category"
                                                dataKey={categoryKey}
                                                fontSize={10}
                                                width={48}
                                                label={{
                                                    value: categoryLabel,
                                                    angle: -90,
                                                    position: "insideLeft",
                                                    offset: -15,
                                                    style: { fontSize: 11, fill: "hsl(var(--muted-foreground))", textAnchor: "middle" },
                                                }}
                                            />
                                            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }}
                                                formatter={(value, name) => [formatChartTooltipValue(value, name, s.chart), name]} />
                                            {seriesList.length > 1 && (
                                                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconSize={8} />
                                            )}
                                            {seriesList.map((sr, idx) => (
                                                <Bar key={sr.key} dataKey={sr.key} name={sr.label || sr.key} stackId={s.chart.stacked ? "a" : undefined}>
                                                    {seriesList.length === 1 ? (
                                                        s.chart.data.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={SERIES_COLORS[index % SERIES_COLORS.length]} />
                                                        ))
                                                    ) : (
                                                        s.chart.data.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={SERIES_COLORS[idx % SERIES_COLORS.length]} />
                                                        ))
                                                    )}
                                                </Bar>
                                            ))}
                                        </BarChart>
                                    </ResponsiveContainer>
                                );
                            }

                            // ────────── BAR / LINE / AREA ──────────
                            const isLine = chartType === "line";
                            const isArea = chartType === "area";
                            const ChartCmp = isArea ? AreaChart : isLine ? LineChart : BarChart;
                            return (
                                <ResponsiveContainer width="100%" height="100%">
                                    <ChartCmp data={s.chart.data} margin={{ top: 8, right: 20, left: 35, bottom: 28 }}>
                                        <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                                        {/* iter-121.45 — Bug 3 fix. Always render
                                            axis labels so users immediately
                                            understand what they're looking at.
                                            X label derives from the LLM-supplied
                                            x_label OR the explicit dimension OR
                                            falls back to the data key. Y label
                                            uses y_label OR, in multi-series mode,
                                            a generic "Value" (the legend already
                                            disambiguates the series). */}
                                        <XAxis
                                            dataKey={categoryKey}
                                            fontSize={10}
                                            label={{
                                                value: s.chart.x_label || s.chart.dimension || "Category",
                                                position: "insideBottom",
                                                offset: -10,
                                                style: { fontSize: 11, fill: "hsl(var(--muted-foreground))" },
                                            }}
                                        />
                                        <YAxis
                                            fontSize={10}
                                            tickFormatter={formatAxisTick}
                                            label={{
                                                value: s.chart.y_label || (seriesList.length === 1 ? (seriesList[0].label || "Value") : "Value"),
                                                angle: -90,
                                                position: "insideLeft",
                                                offset: -15,
                                                style: { fontSize: 11, fill: "hsl(var(--muted-foreground))", textAnchor: "middle" },
                                            }}
                                        />
                                        <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }}
                                            formatter={(value, name) => [formatChartTooltipValue(value, name, s.chart), name]} />
                                        {seriesList.length > 1 && (
                                            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconSize={8} />
                                        )}
                                        {seriesList.map((sr, idx) => {
                                            if (isArea) {
                                                return (
                                                    <Area key={sr.key} type="monotone" dataKey={sr.key}
                                                        name={sr.label || sr.key}
                                                        stroke={SERIES_COLORS[idx % SERIES_COLORS.length]}
                                                        fill={SERIES_COLORS[idx % SERIES_COLORS.length]}
                                                        fillOpacity={0.25} />
                                                );
                                            }
                                            return isLine ? (
                                                <Line key={sr.key} type="monotone" dataKey={sr.key}
                                                    name={sr.label || sr.key}
                                                    stroke={SERIES_COLORS[idx % SERIES_COLORS.length]}
                                                    dot={false} />
                                            ) : (
                                                <Bar key={sr.key} dataKey={sr.key}
                                                    name={sr.label || sr.key}
                                                    stackId={s.chart.stacked ? "a" : undefined}>
                                                    {seriesList.length === 1 ? (
                                                        s.chart.data.map((entry, index) => (
                                                            <Cell
                                                                key={`cell-${index}`}
                                                                fill={SERIES_COLORS[index % SERIES_COLORS.length]}
                                                            />
                                                        ))
                                                    ) : (
                                                        s.chart.data.map((entry, index) => (
                                                            <Cell
                                                                key={`cell-${index}`}
                                                                fill={SERIES_COLORS[idx % SERIES_COLORS.length]}
                                                            />
                                                        ))
                                                    )}
                                                </Bar>
                                            );
                                        })}
                                    </ChartCmp>
                                </ResponsiveContainer>
                            );
                        })()}
                    </div>
                </div>
            )
            }
            {
                s.insights?.length > 0 && (
                    <div>
                        <div className="eyebrow mb-2">Insights</div>
                        <ul className="space-y-1.5">
                            {s.insights.map((ins, i) => (
                                <li key={i} className="text-sm flex gap-2">
                                    <span className="text-primary">→</span>
                                    <span>{ins}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )
            }
            {
                s.followups?.length > 0 && (
                    <div>
                        <div className="eyebrow mb-2">Follow-up</div>
                        <div className="flex flex-wrap gap-1.5">
                            {s.followups.map((f, i) => (
                                <Badge
                                    key={i}
                                    variant="outline"
                                    className="font-normal text-xs cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                                    onClick={(e) => {
                                        // iter-121.45 — Bug 1 fix. Previously the
                                        // Badge had `cursor-pointer` styling but no
                                        // onClick handler so a click did nothing.
                                        // Now we lift the question text into the
                                        // chat input via the orchestrator callback.
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (onFollowupClick) onFollowupClick(f);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if ((e.key === "Enter" || e.key === " ") && onFollowupClick) {
                                            e.preventDefault();
                                            onFollowupClick(f);
                                        }
                                    }}
                                    data-testid={`sense-ai-followup-${i}`}>
                                    {f}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )
            }
        </div >
    );
}
